/*
  Checks if two arrays have the same elements, ignoring the order or not
 */

export const arraysAreEqual = (a: Array<unknown>, b: Array<unknown>, ignoreOrder = true): boolean => {
    if (a === b) return true;
    if (a == null || b == null) return false;
    if (a.length !== b.length) return false;

    let ca = a,
        cb = b;
    if (ignoreOrder) {
        ca = [...a].sort();
        cb = [...b].sort();
    }
    for (let i = 0; i < ca.length; ++i) {
        if (ca[i] !== cb[i]) return false;
    }
    return true;
};

const randomSeeded = (seed: number): number => {
    const x = Math.sin(seed++) * 10000;
    return x - Math.floor(x);
};

/** Shuffles the elements in array *in place* and returns the array. */
export function shuffleArray<T>(array: Array<T>, seed: number): Array<T> {
    // <-- ADDED ARGUMENT
    let m = array.length,
        t,
        i;

    // While there remain elements to shuffle…
    while (m) {
        // Pick a remaining element…
        i = Math.floor(randomSeeded(seed) * m--); // <-- MODIFIED LINE

        // And swap it with the current element.
        t = array[m];
        array[m] = array[i];
        array[i] = t;
        ++seed; // <-- ADDED LINE
    }

    return array;
}

/** Moves the element at fromIndex to toIndex in-place and returns the array. */
export const arrayMove = (arr: Array<string>, fromIndex: number, toIndex: number): Array<string> => {
    if (toIndex >= arr.length) {
        let k = toIndex - arr.length + 1;
        while (k--) {
            arr.push('');
        }
    }
    arr.splice(toIndex, 0, arr.splice(fromIndex, 1)[0]);
    return arr;
};

type ArrayElem<A> = A extends Array<infer Elem> ? Elem : never;
/** Documented here: https://github.com/microsoft/TypeScript/issues/30271#issuecomment-476278582 */
export function makeMembersCallable<T>(array: T): Array<ArrayElem<T>> {
    return array as any;
}

export function linspace(startValue: number, stopValue: number, cardinality: number): Array<number> {
    const arr = [];
    const step = (stopValue - startValue) / (cardinality - 1);
    for (let i = 0; i < cardinality; i++) {
        arr.push(startValue + step * i);
    }
    return arr;
}

export function indexOfMax(arr: Array<number>) {
    if (arr.length === 0) {
        return -1;
    }

    let max = arr[0];
    let maxIndex = 0;

    for (let i = 1; i < arr.length; i++) {
        if (arr[i] > max) {
            maxIndex = i;
            max = arr[i];
        }
    }

    return maxIndex;
}

export const allStringsSameLength = (arr: Array<string>) => {
    if (arr.length === 0) {
        return true;
    }

    const firstStringLength = arr[0].length;

    for (let i = 1; i < arr.length; i++) {
        if (arr[i].length !== firstStringLength) {
            return false;
        }
    }

    return true;
};

export const arrayMinMax = (arr: any[]) =>
    arr.reduce(
        ([min, max], val) => [Math.min(min, val), Math.max(max, val)],
        [Number.POSITIVE_INFINITY, Number.NEGATIVE_INFINITY],
    );

/** `worker` over `items` with at most `concurrency` calls in flight; results keep the items' order. */
export async function mapWithConcurrency<T, R>(
    items: readonly T[],
    concurrency: number,
    worker: (item: T) => Promise<R>,
): Promise<R[]> {
    const results: R[] = new Array(items.length);
    let nextIndex = 0;
    const runners = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
        while (nextIndex < items.length) {
            const index = nextIndex;
            nextIndex += 1;
            results[index] = await worker(items[index]);
        }
    });
    await Promise.all(runners);
    return results;
}
